import React, { useState } from 'react';
import Badge from 'antd/lib/badge';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Menu from 'antd/lib/menu';
import { EllipsisOutlined } from '@ant-design/icons';

import { Workspace } from 'reducers';
import { t } from 'utils/i18n';

interface Props {
    menu: JSX.Element;
    workspace: Workspace;
    is3D: boolean;
    hasGuide: boolean;
    onWorkspace(ws: Workspace): void;
    onFullscreen(): void;
    onInfo(): void;
    onFilters(): void;
    onGuide(): void;
    onSettings?(): void;
    onShortcuts?(): void;
    filtersActive?: boolean;
    frameDeleted?: boolean;
    onRestoreFrame?(): void;
    onDeleteFrame?(): void;
    onCopyFrameLink?(): void;
}

// A leaf item of the outer menu or of the nested Job menu; submenu titles also have role=menuitem.
function isLeafItem(target: EventTarget): boolean {
    if (!(target instanceof Element)) return false;
    const item = target.closest('[role="menuitem"]');
    return !!item && !item.hasAttribute('aria-haspopup') && item.getAttribute('aria-disabled') !== 'true';
}

export default function MoreMenu(props: Props): JSX.Element {
    const {
        menu, workspace, is3D, hasGuide, onWorkspace, onFullscreen, onInfo, onFilters, onGuide,
        onSettings, onShortcuts, filtersActive, frameDeleted, onRestoreFrame, onDeleteFrame, onCopyFrameLink,
    } = props;
    const [visible, setVisible] = useState(false);
    const workspaces = Object.values(Workspace)
        .filter((ws) => (is3D ? ws === Workspace.STANDARD3D : ws !== Workspace.STANDARD3D));

    // The nested Job menu swallows rc-menu's item click, so rc-dropdown never closes by itself;
    // React click events bubble through the submenu portal, so one capture handler sees them all.
    const overlay = (
        <Menu
            className='cvat-workspace-more-menu'
            selectedKeys={[`ws:${workspace}`]}
            // keyboard activation fires no click event; outer items only, the Job menu overrides this
            onClick={() => setVisible(false)}
            onClickCapture={(event: React.MouseEvent) => {
                if (isLeafItem(event.target)) setVisible(false);
            }}
        >
            <Menu.SubMenu key='job' title={t('Job')}>{menu}</Menu.SubMenu>
            {/* a group, not a submenu: one click less, and the items are always rendered */}
            <Menu.ItemGroup key='workspace' title={t('Workspace')}>
                {workspaces.map((ws) => (
                    <Menu.Item key={`ws:${ws}`} onClick={() => onWorkspace(ws)}>{t(ws)}</Menu.Item>
                ))}
            </Menu.ItemGroup>
            <Menu.Divider />
            <Menu.Item key='filters' onClick={onFilters}>
                {filtersActive ? <Badge dot offset={[6, 0]}>{t('Filters')}</Badge> : t('Filters')}
            </Menu.Item>
            <Menu.Item key='info' onClick={onInfo}>{t('Info')}</Menu.Item>
            {hasGuide && <Menu.Item key='guide' onClick={onGuide}>{t('Guide')}</Menu.Item>}
            {frameDeleted && onRestoreFrame && (
                <Menu.Item key='restore-frame' onClick={onRestoreFrame}>{t('Restore frame')}</Menu.Item>
            )}
            {!frameDeleted && onDeleteFrame && (
                <Menu.Item key='delete-frame' onClick={onDeleteFrame} danger>{t('Delete frame')}</Menu.Item>
            )}
            {onCopyFrameLink && <Menu.Item key='copy-frame-link' onClick={onCopyFrameLink}>{t('Copy frame link')}</Menu.Item>}
            <Menu.Item key='fullscreen' onClick={onFullscreen}>{t('Fullscreen')}</Menu.Item>
            {(onSettings || onShortcuts) && <Menu.Divider />}
            {onSettings && <Menu.Item key='settings' onClick={onSettings}>{t('Settings')}</Menu.Item>}
            {onShortcuts && <Menu.Item key='shortcuts' onClick={onShortcuts}>{t('Keyboard shortcuts')}</Menu.Item>}
        </Menu>
    );

    return (
        <Dropdown trigger={['click']} overlay={overlay} destroyPopupOnHide visible={visible} onVisibleChange={setVisible}>
            <Button
                type='text'
                aria-label={t('More')}
                className='cvat-workspace-more-button'
                data-filters-active={!!filtersActive}
                icon={<Badge dot={!!filtersActive}><EllipsisOutlined /></Badge>}
            />
        </Dropdown>
    );
}
